import { ApiClient, ApiResponse } from '../client';
import { Order, ListOptions, OrderStatus, PaymentStatus } from '../models';

/**
 * Datos para crear un pedido
 */
export interface CreateOrderData {
  products: Array<{
    productId: string;
    quantity: number;
    variantSku?: string;
  }>;
  customerId?: string;
  shippingAddress: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  billingAddress?: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  shippingMethod: string;
  paymentMethod: string;
  notes?: string;
  customerInfo?: {
    name: string;
    email: string;
    phone?: string;
    nif?: string;
  };
  dropShipping?: {
    enabled: boolean;
    notifyRecipient: boolean;
    recipient: {
      name: string;
      email?: string;
      phone?: string;
      notes?: string;
      address: {
        street: string;
        city: string;
        postalCode: string;
        country: string;
      };
    };
  };
}

/**
 * Servicio para gestionar pedidos
 */
export class OrderService {
  private client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Obtener pedidos del usuario actual o todos (para admin)
   * @param options Opciones de filtrado y paginación
   */
  public async getOrders(options: ListOptions = {}): Promise<ApiResponse<Order[]>> {
    const { page, limit, sort, order, search, filter } = options;

    // Construir parámetros de query
    const params: Record<string, any> = {
      page,
      limit,
      sort,
      order
    };

    if (search) {
      params.search = search;
    }

    // Añadir filtros específicos
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        params[key] = value;
      });
    }

    return await this.client.get<Order[]>('/orders', { params });
  }

  /**
   * Obtener un pedido específico por ID
   * @param id ID del pedido
   */
  public async getOrder(id: string): Promise<ApiResponse<Order>> {
    return await this.client.get<Order>(`/orders/${id}`);
  }

  /**
   * Crear un nuevo pedido (checkout)
   * @param orderData Datos del pedido
   */
  public async createOrder(orderData: CreateOrderData): Promise<ApiResponse<Order>> {
    return await this.client.post<Order>('/orders', orderData);
  }

  /**
   * Iniciar proceso de checkout
   * Verifica disponibilidad y reserva inventario temporalmente
   */
  public async initiateCheckout(): Promise<ApiResponse<any>> {
    return await this.client.post<any>('/orders/checkout', {});
  }

  /**
   * Actualizar el estado de un pedido (admin/comercial)
   * @param id ID del pedido
   * @param status Nuevo estado
   */
  public async updateOrderStatus(
    id: string,
    status: OrderStatus
  ): Promise<ApiResponse<Order>> {
    return await this.client.put<Order>(`/orders/${id}`, { status });
  }

  /**
   * Actualizar el estado de pago de un pedido (admin/comercial)
   * @param id ID del pedido
   * @param paymentStatus Nuevo estado de pago
   */
  public async updatePaymentStatus(
    id: string,
    paymentStatus: PaymentStatus
  ): Promise<ApiResponse<Order>> {
    return await this.client.put<Order>(`/orders/${id}`, { paymentStatus });
  }

  /**
   * Actualizar información general de un pedido
   * @param id ID del pedido
   * @param data Datos a actualizar
   */
  public async updateOrder(id: string, data: Partial<Order>): Promise<ApiResponse<Order>> {
    return await this.client.put<Order>(`/orders/${id}`, data);
  }

  /**
   * Añadir número de seguimiento a un pedido (admin/comercial)
   * @param id ID del pedido
   * @param trackingNumber Número de seguimiento
   */
  public async addTrackingNumber(id: string, trackingNumber: string): Promise<ApiResponse<Order>> {
    return await this.client.put<Order>(`/orders/${id}`, { trackingNumber });
  }

  /**
   * Cancelar un pedido
   * @param id ID del pedido
   * @param reason Motivo de cancelación
   */
  public async cancelOrder(id: string, reason?: string): Promise<ApiResponse<Order>> {
    return await this.client.put<Order>(`/orders/${id}`, {
      status: 'cancelled',
      notes: reason ? `Cancelado: ${reason}` : 'Cancelado por el cliente'
    });
  }

  /**
   * Obtener historial de pedidos del usuario actual
   */
  public async getOrderHistory(): Promise<ApiResponse<Order[]>> {
    return await this.client.get<Order[]>('/orders/history');
  }

  /**
   * Simular procesamiento de pago (para desarrollo/testing)
   * @param orderId ID del pedido
   * @param paymentDetails Detalles del pago
   */
  public async simulatePayment(
    orderId: string,
    paymentDetails: {
      method: string;
      success: boolean;
    }
  ): Promise<ApiResponse<any>> {
    return await this.client.post<any>(`/orders/${orderId}/payment/simulate`, paymentDetails);
  }

  /**
   * Generar factura para un pedido
   * @param orderId ID del pedido
   */
  public async generateInvoice(orderId: string): Promise<ApiResponse<{ invoiceUrl: string }>> {
    return await this.client.post<{ invoiceUrl: string }>(`/orders/${orderId}/invoice`, {});
  }

  /**
   * Obtener presupuesto de envío (coste y ETA) para los items y dirección dados
   */
  public async shippingQuote(payload: {
    items: Array<{ productId: string; quantity: number }>;
    shippingAddress: { country: string; postalCode?: string; city?: string; street?: string };
  }): Promise<ApiResponse<{ shippingCost: number; currency: string; etaDays: number }>> {
    return await this.client.post<{ shippingCost: number; currency: string; etaDays: number }>(
      '/orders/shipping-quote',
      payload
    );
  }

  /**
   * Obtener estadísticas de pedidos (admin/beeadmin)
   */
  public async getOrderStats(): Promise<ApiResponse<any>> {
    return await this.client.get<any>('/orders/statistics');
  }

  /**
   * Fusionar pedidos
   */
  public async mergeOrders(masterOrderId: string, mergeOrderIds: string[]): Promise<ApiResponse<Order>> {
    return await this.client.post<Order>('/orders/merge', { masterOrderId, mergeOrderIds });
  }

  /**
   * Actualización avanzada de pedido (Admin)
   */
  public async updateOrderAdmin(id: string, data: any): Promise<ApiResponse<Order>> {
    return await this.client.put<Order>(`/orders/${id}/admin`, data);
  }

  /**
   * Actualizar fecha de entrega estimada (Admin)
   */
  public async updateEstimatedDelivery(id: string, estimatedDelivery: string): Promise<ApiResponse<Order>> {
    return await this.client.put<Order>(`/orders/${id}/estimated-delivery`, { estimatedDelivery });
  }
} 
